import React, { memo, useState } from "react";
import { ResultImageEntity } from "~/types/models/dashboard";
import { useAppDispatch, useAppSelector } from "~/hooks";
import {
  selectImage,
  selectResultImageById,
  setFavorite,
  setShowGoToFavPopup,
  voteImage,
} from "~/redux/dashboards/slice";
import { toggleFavorite, toggleVoting } from "~/redux/dashboards/thunk";
import { requestOK } from "redux-thunk-kit";
import { Col, Row, UiBox, UiCenter, UiImage, UiText, UiWrapItem } from "~/components/elements";
import { LazyLoadImage } from "react-lazy-load-image-component";
import loadingPlaceholder from "~/assets/img/loading.webp";
import heartChecked from "~/assets/img/v2/heart_checked.png";
import heartUncheck from "~/assets/img/v2/heart_uncheck.png";
import { ThumbUpFilledIcon, ThumbUpOutlineIcon } from "~/components/icons/SvgIcons";

export const ImageThumbnail = memo(
  ({
    imageId,
    idx,
    onClick,
    scrollPosition,
    favoriteable = true,
    votable = false,
    maxSize = 12.5,
  }: {
    imageId: string;
    scrollPosition: any;
    idx: number;
    onClick?: (image: ResultImageEntity, idx: number) => void;
    favoriteable?: boolean;
    votable?: boolean;
    maxSize?: number;
  }) => {
    const dispatch = useAppDispatch();
    const image = useAppSelector(state => selectResultImageById(state, imageId));
    const [isDirty, setIsDirty] = useState(false);
    const [isVoteDirty, setIsVoteDirty] = useState(false);

    const onClickFavorite = async (isFavorite: boolean) => {
      setIsDirty(true);
      dispatch(setFavorite({ imageId, isFavorite }));
      dispatch(selectImage({ imageId, isSelected: isFavorite }));
      if (isFavorite) {
        dispatch(setShowGoToFavPopup({ isShow: true }));
      }
      const resAction = await dispatch(toggleFavorite({ data: { is_favorited: isFavorite }, imageID: imageId }));
      if (!requestOK(resAction, toggleFavorite)) {
        dispatch(setFavorite({ imageId, isFavorite: !isFavorite }));
      }
    };

    const onClickSelectCheckbox = async (isSelected: boolean) => {
      dispatch(selectImage({ imageId, isSelected }));
    };

    let animateClass: string;
    let animateVoteClass: string;
    if (isDirty) {
      animateClass = image?.is_favorited ? "animate__animated animate__bounceIn" : "";
    } else {
      animateClass = "";
    }

    if (isVoteDirty) {
      animateVoteClass = image?.$isLiked
        ? "animate__animated animate__rotateInDownLeft animate__faster"
        : "animate__animated animate__rotateIn animate__faster";
    } else {
      animateVoteClass = "";
    }

    const onClickVote = async () => {
      setIsVoteDirty(true);
      const newLikeValue = !image?.$isLiked;
      dispatch(voteImage({ imageId, isLiked: newLikeValue }));
      const resAction = await dispatch(toggleVoting({ data: { like: newLikeValue }, imageID: imageId }));
      if (!requestOK(resAction, toggleVoting)) {
        dispatch(voteImage({ imageId, isLiked: newLikeValue }));
      }
    };

    return (
      <UiWrapItem>
        <Col
          align="center"
          maxW={{ base: "undefined", sm: `${maxSize}rem` }}
          minW={{ base: "undefined", sm: `${maxSize * 0.85}rem` }}
          minH={{ base: "undefined", sm: `${maxSize * 0.85}rem` }}
        >
          <UiBox cursor={onClick ? "pointer" : "default"} width="100% !important" pos="relative" lineHeight={0}>
            <LazyLoadImage
              className="thumbnailImage"
              alt=""
              src={image?.image_url}
              // src={loadingPlaceholder}
              width="100%"
              height="100%"
              style={{ borderRadius: "0.75rem", objectFit: "cover" }}
              scrollPosition={scrollPosition}
              placeholderSrc={loadingPlaceholder}
              onClick={() => {
                onClick?.(image!, idx);
              }}
            />

            <Row justifyContent="flex-end" pos="absolute" top={2} left={0} right={0} alignItems="flex-start" px={2}>
              {/* <UiCheckbox isChecked={image?.isSelected} onChange={({ target }) => onClickSelectCheckbox(target.checked)} /> */}
              {favoriteable && (
                <UiImage
                  src={image?.is_favorited ? heartChecked : heartUncheck}
                  w="1.5rem"
                  h="1.5rem"
                  onClick={() => onClickFavorite(!image?.is_favorited)}
                  className={animateClass}
                />
              )}
            </Row>
          </UiBox>

          {votable && (
            <UiCenter
              onClick={onClickVote}
              mt={2}
              rounded="full"
              bg="lightblue"
              cursor="pointer"
              // aspectRatio={1}
              // maxH="100%"
              w={{ base: "full", sm: "70%" }}
              py={2}
            >
              <Row align="center" justify="center">
                {image?.$isLiked ? (
                  <ThumbUpFilledIcon className={animateVoteClass} boxSize={6} />
                ) : (
                  <ThumbUpOutlineIcon className={animateVoteClass} boxSize={6} />
                )}
                <UiText vr="title1" ml={4} color="neutral.gray700">
                  {image?.like_count}
                </UiText>
              </Row>
            </UiCenter>
          )}
        </Col>
      </UiWrapItem>
    );
  }
);
